/**
 * What to do when an app-initiated back navigation gave up waiting (#4707).
 *
 * The app never removes a page directly. It calls `history.back()` and waits for the
 * browser to fire a `popstate` event; the code that removes the page runs inside that
 * event's handler. `BrowserHistory.navigateBackAsync` races that event against a
 * 5-second safety timer, and this module decides what happens when the timer wins.
 *
 * The dangerous case, and the reason this exists: the browser can be sitting on the
 * OLDEST entry in its history while the app still has several pages open. There,
 * `history.back()` is a no-op — the browser does nothing and fires no `popstate` at
 * all — so the page is never removed, and every further press repeats that. The user
 * sees a page whose close button and system back button do nothing, with no message,
 * and force-quitting the app is the only way out. Sentry's `PROBLEM: NavBackTimedOut`
 * measures exactly this state.
 *
 * The decision rests on one fact the caller already tracks: did the `popstate` handler
 * ever START. `BrowserHistory.pendingBackHandlerStarted` is set at the very top of
 * `onPopState`, before any `await`, so it is true the moment the event arrives.
 */

export enum BackTimeoutAction {
  /** The browser fired nothing, so nothing popped the page. Remove it directly. */
  RecoverByDirectPop = 'recover-by-direct-pop',
  /**
   * The `popstate` DID arrive and its handler is merely slow — nearly always a
   * user-facing "discard changes?" dialog the user is sitting on for more than five
   * seconds. Popping here would close the page out from under an open dialog, so this
   * case is reported and otherwise left alone.
   */
  ReportOnly = 'report-only',
  /** Only the root page is open. Popping it would leave the app with no page at all. */
  NothingToPop = 'nothing-to-pop',
}

export interface BackTimeoutInput {
  /** Whether the `popstate` handler began running for this back navigation. */
  handlerStarted: boolean;
  /** How many pages the app currently has open (`NavController.length()`). */
  navDepth: number;
}

/** Decide what a timed-out back navigation should do. See BackTimeoutAction. */
export function resolveBackTimeoutAction(input: BackTimeoutInput): BackTimeoutAction {
  // if the handler started, the browser did its part and only the app is slow
  if (input.handlerStarted) {
    return BackTimeoutAction.ReportOnly;
  }
  // if nothing but the root page is open, there is nothing to close
  if (input.navDepth <= 1) {
    return BackTimeoutAction.NothingToPop;
  }
  return BackTimeoutAction.RecoverByDirectPop;
}
